import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { supabase } from './supabase'
import { useRoster } from './roster'

/**
 * Agent photographs.
 *
 * The `headshots` bucket is private on purpose — 785 agents' photographs are
 * not something to leave publicly enumerable — so every image needs a signed
 * URL, and a signed URL expires. This signs the whole roster in a few parallel
 * requests rather than one per agent, and signs again before the links go
 * stale.
 *
 * A photo that will not sign, will not load, or does not exist is not an error
 * anyone needs to see. The initials that were on screen before photos existed
 * are a perfectly good answer, and that is what happens in every failure case.
 *
 * Two things here are deliberate and easy to undo by accident:
 *
 * - **A refresh merges, it does not replace.** A link with an hour left on it
 *   beats no picture at all, so a failed renewal keeps the working links it
 *   already had rather than blanking the roster back to initials.
 * - **A new signature is a new URL**, and therefore a fresh download of every
 *   visible photo. That is why the links are signed for most of a working day
 *   instead of the hour that first suggested itself.
 */

const TTL_SECONDS = 8 * 3600
const RESIGN_AFTER_MS = 7 * 3600 * 1000
const RETRY_AFTER_MS = 60 * 1000
const CHUNK = 250

const HeadshotContext = createContext<Map<string, string>>(new Map())

export function HeadshotProvider({ children }: { children: ReactNode }) {
  const { agents } = useRoster()
  const [urls, setUrls] = useState<Map<string, string>>(new Map())
  const [round, setRound] = useState(0)

  /**
   * rezen_id -> storage key, for the agents that actually have a photo.
   *
   * This is stable because `agents` is set once and never replaced. If the
   * roster ever gains a refresh, check here first: a new `agents` array signs
   * all 625 URLs again.
   */
  const paths = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of agents ?? []) {
      if (a.headshot_path) m.set(a.rezen_id, a.headshot_path)
    }
    return m
  }, [agents])

  useEffect(() => {
    if (paths.size === 0) return
    let cancelled = false
    let retry = 0

    const owner = new Map<string, string>() // storage key -> rezen_id
    for (const [id, path] of paths) owner.set(path, id)

    const chunks: string[][] = []
    const keys = Array.from(owner.keys())
    for (let i = 0; i < keys.length; i += CHUNK) chunks.push(keys.slice(i, i + CHUNK))

    void (async () => {
      const results = await Promise.all(
        chunks.map((c) => supabase.storage.from('headshots').createSignedUrls(c, TTL_SECONDS)),
      )
      if (cancelled) return

      const next = new Map<string, string>()
      let failed = false
      for (const { data, error } of results) {
        if (error || !data) {
          failed = true
          continue
        }
        for (const row of data) {
          if (row.error || !row.signedUrl || !row.path) {
            failed = true
            continue
          }
          const id = owner.get(row.path)
          if (id) next.set(id, row.signedUrl)
        }
      }

      setUrls((prev) => {
        const merged = new Map<string, string>()
        for (const id of paths.keys()) {
          const u = next.get(id) ?? prev.get(id)
          if (u) merged.set(id, u)
        }
        return merged
      })

      // Signing is the one step that can fail quietly, so it gets a second go
      // rather than waiting out the full refresh interval.
      if (failed) retry = window.setTimeout(() => setRound((n) => n + 1), RETRY_AFTER_MS)
    })()

    return () => {
      cancelled = true
      window.clearTimeout(retry)
    }
  }, [paths, round])

  useEffect(() => {
    const t = window.setInterval(() => setRound((n) => n + 1), RESIGN_AFTER_MS)
    return () => window.clearInterval(t)
  }, [])

  return <HeadshotContext.Provider value={urls}>{children}</HeadshotContext.Provider>
}

export function useHeadshot(rezenId: string): string | undefined {
  return useContext(HeadshotContext).get(rezenId)
}
